
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface UseWorkoutPlanRealtimeProps {
  planId: string | undefined;
  planRoutines: any[] | undefined;
  updateExerciseField: (routineId: string, exerciseId: string, field: string, value: any) => void;
  handleStructuralRefetch: () => Promise<void>;
  handleMinimalRefetch: () => void;
}

export const useWorkoutPlanRealtime = ({
  planId,
  planRoutines,
  updateExerciseField,
  handleStructuralRefetch,
  handleMinimalRefetch
}: UseWorkoutPlanRealtimeProps) => {
  // ✅ NAVIGATION-PRESERVING realtime with intelligent coordination
  useEffect(() => {
    if (!planId || !planRoutines?.length) return;
    
    const planChannel = supabase
      .channel(`plan-${planId}-navigation-optimized`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'workout_plan_routines',
          filter: `workout_plan_id=eq.${planId}`
        },
        (payload) => {
          console.log(`🔄 Plan structure change (navigation preserved):`, payload);
          handleStructuralRefetch();
        }
      )
      .subscribe();
      
    const routineIds = planRoutines.map(r => r.id);
    let exercisesChannel: any;
    
    if (routineIds.length > 0) {
      exercisesChannel = supabase
        .channel(`plan-${planId}-exercises-navigation-optimized`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'exercises',
            filter: `routine_id=in.(${routineIds.join(',')})`
          },
          (payload) => {
            if (payload.new && payload.old) {
              const exerciseId = payload.new.id;
              const routineId = payload.new.routine_id;
              
              const changedFields = Object.keys(payload.new).filter(key => 
                payload.new[key] !== payload.old[key] && key !== 'updated_at' && key !== 'id'
              );
              
              console.log(`🎯 Navigation-preserving granular update: ${exerciseId}`, changedFields);
              
              changedFields.forEach(fieldName => {
                updateExerciseField(routineId, exerciseId, fieldName, payload.new[fieldName]);
              });
              
              // Use minimal refetch to preserve navigation state
              handleMinimalRefetch();
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'exercises',
            filter: `routine_id=in.(${routineIds.join(',')})`
          },
          () => {
            console.log(`🎯 Exercise added - using minimal refetch to preserve navigation`);
            // Use minimal refetch instead of structural for new exercises to preserve tab state
            handleMinimalRefetch();
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'exercises',
            filter: `routine_id=in.(${routineIds.join(',')})`
          },
          () => {
            console.log(`🔄 Exercise deleted - structural refetch needed`);
            handleStructuralRefetch();
          }
        )
        .subscribe();
    }
    
    return () => {
      supabase.removeChannel(planChannel);
      if (exercisesChannel) {
        supabase.removeChannel(exercisesChannel);
      }
    };
  }, [planId, planRoutines, updateExerciseField, handleStructuralRefetch, handleMinimalRefetch]);
};
